import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Background from './components/Background';

// Lazy load components
const SignIn = lazy(() => import('./components/SignIn'));
const VibeFeed = lazy(() => import('./components/VibeFeed'));
const DesktopLandingPage = lazy(() => import('./components/DesktopLandingPage'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));

// Memoized PostCard component
const PostCard = React.memo(({
  user,
  time,
  emojis,
  text,
}: {
  user: string;
  time: string;
  emojis: string[];
  text: string;
}) => {
  return (
    <div className="w-[90vw] max-w-xs rounded-2xl bg-gradient-to-br from-feelr-gold via-yellow-400 to-feelr-gold-dark p-5 shadow-lg flex flex-col justify-between min-h-[160px] relative overflow-hidden">
      <div className="flex justify-between items-center mb-2">
        <span className="font-display font-bold text-feelr-black text-base">{user}</span>
        <span className="font-display text-feelr-black/70 text-sm">{time}</span>
      </div>
      <div className="flex items-center justify-center gap-3 text-5xl mb-2">
        {emojis.map((e, i) => (
          <span key={i}>{e}</span>
        ))}
      </div>
      <div className="font-display font-extrabold text-feelr-black text-xl leading-snug">{text}</div>
    </div>
  );
});

PostCard.displayName = 'PostCard';

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-feelr-gold"></div>
  </div>
);

const App: React.FC = () => {
  const [currentScreen, setCurrentScreen] = useState<'splash' | 'signin' | 'main' | 'admin'>('splash');

  // Memoize the session check function
  const checkSession = useCallback(() => {
    const token = localStorage.getItem('feelr_token');
    const authData = localStorage.getItem('feelr_auth');
    const username = localStorage.getItem('feelr_username');
    
    if (token && authData && username) {
      try {
        const auth = JSON.parse(authData);
        if (auth.sessionExpires && new Date(auth.sessionExpires) > new Date()) {
          setCurrentScreen('main');
        } else {
          localStorage.removeItem('feelr_token');
          localStorage.removeItem('feelr_auth');
          localStorage.removeItem('feelr_username');
        }
      } catch (error) {
        localStorage.removeItem('feelr_token');
        localStorage.removeItem('feelr_auth');
        localStorage.removeItem('feelr_username');
      }
    }
  }, []);

  // Check for existing user session on app load
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const handleJoinFeelr = useCallback(() => {
    setCurrentScreen('signin');
  }, []);

  // Memoize the splash screen content
  const splashContent = (
    <motion.div
      key="splash"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen w-full relative overflow-hidden"
    >
      {/* Desktop landing page */}
      <div className="feelr-desktop-warning hidden">
        <Suspense fallback={<LoadingSpinner />}>
          <DesktopLandingPage />
        </Suspense>
      </div>

      {/* Mobile splash screen container */}
      <div className="feelr-mobile-splash min-h-screen w-full flex flex-col items-center justify-between relative overflow-hidden">
        {/* Main content */}
        <div className="relative z-10 flex flex-col w-full h-full flex-1 items-center justify-between py-8">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="mb-4 mt-2"
          >
            <h1 className="text-5xl font-display font-bold gradient-text tracking-tight">Feelr</h1>
          </motion.div>
          {/* Slogan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-8 text-center px-4"
          >
            <div className="text-2xl sm:text-3xl font-display font-extrabold text-feelr-gold mb-2 leading-tight">No Likes.</div>
            <div className="text-2xl sm:text-3xl font-display font-extrabold text-feelr-gold mb-2 leading-tight">No Follows.</div>
            <div className="text-2xl sm:text-3xl font-display font-extrabold text-feelr-gold leading-tight whitespace-nowrap">Just Vibes.</div>
          </motion.div>
          {/* Example post cards */}
          <div className="flex flex-col gap-4 w-full items-center mb-4">
            <PostCard
              user="james_"
              time="3m"
              emojis={["😔", "❤️"]}
              text={"kinda overwhelmed\nbut hopeful"}
            />
            <PostCard
              user="alexis"
              time="7m"
              emojis={["😃", "🌈"]}
              text="feeling bright and inspired"
            />
          </div>
          {/* Inviting subtext */}
          <div className="text-sm text-feelr-gold/90 font-medium text-center max-w-xs mb-6 mx-auto font-display tracking-wide leading-snug">
            Escape the pressure of likes and followers.<br />
            <span className="block mt-1 font-medium text-feelr-gold">Feelr is your space for real vibes.</span>
          </div>
          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            onClick={handleJoinFeelr}
            className="group w-full max-w-[320px] mb-2 focus:outline-none rounded-full overflow-hidden shadow-lg border-0 p-0"
            style={{ background: 'linear-gradient(90deg, #FFD700 0%, #FFB300 100%)', boxShadow: '0 0 32px 4px rgba(255, 215, 0, 0.25)' }}
          >
            <span className="block w-full px-10 py-5 font-display font-extrabold text-lg sm:text-xl text-feelr-black drop-shadow text-center">Join Feelr</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-feelr-black relative overflow-hidden">
      <Background />
      
      <AnimatePresence mode="wait">
        {currentScreen === 'splash' && splashContent}
        
        {currentScreen === 'signin' && (
          <motion.div
            key="signin"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Suspense fallback={<LoadingSpinner />}>
              <SignIn onSignInSuccess={() => setCurrentScreen('main')} />
            </Suspense>
          </motion.div>
        )}
        
        {currentScreen === 'main' && (
          <motion.div
            key="main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative"
          >
            <Suspense fallback={<LoadingSpinner />}>
              <VibeFeed />
            </Suspense>
          </motion.div>
        )}
        
        {currentScreen === 'admin' && (
          <motion.div
            key="admin"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative"
          >
            <Suspense fallback={<LoadingSpinner />}>
              <AdminDashboard />
            </Suspense>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default React.memo(App);

// Add mobile-only CSS in index.css or a global style file:
// @media (min-width: 600px) {
//   .feelr-mobile-splash { display: none !important; }
//   .feelr-desktop-warning { display: flex !important; }
// } 